import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Shell } from "./Shell";
import { FadeUp } from "./Sections";

const BUILDING_IMG = "https://harcresearch.org/wp-content/uploads/2020/08/about-image-2.jpg";

const HISTORY = [
  { year: "1982", text: "Founded by George P. Mitchell as a nonprofit research and technology organization." },
  { year: "2001", text: "HARC sharpens its focus on sustainability and science-driven solutions." },
  { year: "2017", text: "HARC opens its Living Lab headquarters, demonstrating sustainability in practice." },
  { year: "Today", text: "HARC brings together science, strategy, partners, and implementation to solve complex challenges across Texas and beyond." },
];

const PEOPLE_LINKS = [
  { label: "Meet Our Team", note: "Team directory, powered by the People content model" },
  { label: "Board, Advisory Council, and Fellows", note: "Governance and advisors" },
  { label: "Our Living Lab", note: "HARC's LEED-certified headquarters" },
];

export default function About() {
  return (
    <Shell>
      <div data-testid="about-page">
        <section className="mx-auto max-w-7xl px-6 pt-16 md:pt-24">
          <FadeUp>
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
              About HARC
            </p>
            <h1 className="harc-heading max-w-3xl text-4xl font-medium leading-[1.1] tracking-tight text-[#004778] md:text-6xl">
              Science, people, and practical solutions, together
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#5C6B72] md:text-lg md:font-light">
              HARC brings science, people, and practical solutions together to address complex
              challenges and create lasting impact.
            </p>
            <Link
              to="/impact"
              className="mockup-link-arrow mt-8"
              data-testid="about-explore-impact"
            >
              Explore Our Impact <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </FadeUp>
          <FadeUp delay={0.12} className="mt-12">
            <img
              src={BUILDING_IMG}
              alt="HARC's Living Lab headquarters building"
              className="aspect-[21/9] w-full border border-[#004778]/20 object-cover"
              data-testid="about-building-image"
            />
          </FadeUp>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-24 md:py-32" data-testid="about-purpose">
          <div className="grid gap-12 md:grid-cols-12">
            <FadeUp className="md:col-span-5">
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
                Science With Purpose
              </p>
              <h2 className="harc-heading text-3xl font-medium leading-tight text-[#004778] md:text-4xl">
                Knowledge into action
              </h2>
              <p className="mt-6 text-base leading-relaxed text-[#5C6B72]">
                HARC believes the best solutions begin with credible science, but they shouldn't end
                there. We exist to turn knowledge into action by connecting scientific insight with
                the people, resources, and strategies needed to solve real-world challenges.
              </p>
              <div className="mt-8 border-l-4 border-[#FFA100] bg-[#004778]/5 p-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#004778]">
                  Our Mission
                </p>
                <p className="mt-2 text-sm italic text-[#5C6B72]">
                  [HARC CONTENT REQUIRED: approved mission statement]
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.15} className="md:col-span-6 md:col-start-7">
              <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.16em] text-[#5C6B72]">
                Our History
              </p>
              <ol className="space-y-0" data-testid="about-history">
                {HISTORY.map((h) => (
                  <li key={h.year} className="flex gap-6 border-t border-[#004778]/20 py-5">
                    <span className="harc-heading w-16 shrink-0 text-lg font-semibold text-[#FFA100]">
                      {h.year}
                    </span>
                    <span className="text-sm leading-relaxed text-[#5C6B72]">{h.text}</span>
                  </li>
                ))}
                <li className="border-t border-[#004778]/20" aria-hidden="true" />
              </ol>
            </FadeUp>
          </div>
        </section>

        <section className="bg-[#EEF3F5] py-24 md:py-32" data-testid="about-people">
          <div className="mx-auto max-w-7xl px-6">
            <FadeUp>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-[#004778]">
                Our People
              </p>
              <h2 className="harc-heading max-w-2xl text-3xl font-medium leading-tight text-[#004778] md:text-4xl">
                The People Behind the Science
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C6B72]">
                HARC brings together researchers, scientists, strategists, and experts committed to
                turning knowledge into meaningful solutions.
              </p>
            </FadeUp>
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {PEOPLE_LINKS.map((p, i) => (
                <FadeUp key={p.label} delay={i * 0.08}>
                  <a
                    href="#top"
                    className="group block h-full border border-[#004778]/25 bg-white p-7 transition-colors hover:border-[#004778]"
                    data-testid={`about-people-link-${i + 1}`}
                  >
                    <p className="harc-heading text-xl leading-snug text-[#022A42]">{p.label}</p>
                    <p className="mt-3 text-xs leading-relaxed text-[#5C6B72]">{p.note}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-[#004778]">
                      Explore
                      <ArrowRight
                        size={13}
                        className="text-[#FFA100] transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </a>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.2}>
              <Link
                to="/start-a-conversation"
                className="mockup-link-arrow mt-12"
                data-testid="about-partner-cta"
              >
                Partner With HARC <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </FadeUp>
          </div>
        </section>
      </div>
    </Shell>
  );
}
